import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import type {EditorView} from "@tiptap/pm/view";
import {getPrintoutRegionKind} from "../../blocks/headerFooter/printoutRegionOptions";

const PRINTOUT_DROP_CURSOR_CLASS = "printout-drop-cursor";
const PRINTOUT_DROP_CURSOR_WIDTH = 2;

// Block gaps span the destination's content box; neighbours only determine the vertical position.
// Text positions keep a caret. The overlay stays out of the document, which matters because
// pagination re-measures on every document change.
const drawPrintoutDropCursor = (view: EditorView, position: number, element: HTMLElement) => {
    const $position = view.state.doc.resolve(position);
    const editorElement = view.dom as HTMLElement;
    const editorRect = editorElement.getBoundingClientRect();
    const scaleX = editorRect.width / editorElement.offsetWidth;
    const scaleY = editorRect.height / editorElement.offsetHeight;
    // Pagination lifts regions out of the flow to their page band, so averaging the gap against one
    // would float the line halfway down the empty page instead of marking the body's own edge.
    const flowNeighbour = (node: ProseMirrorNode | null) => (
        !node || $position.parent.inlineContent || getPrintoutRegionKind(node.type.name) ? null : node
    );
    const nodeBefore = flowNeighbour($position.nodeBefore);
    const nodeAfter = flowNeighbour($position.nodeAfter);
    const neighbour = (nodeBefore || nodeAfter)
        ? view.nodeDOM(position - (nodeBefore ? nodeBefore.nodeSize : 0)) as HTMLElement | null
        : null;
    let rect: {left: number; right: number; top: number; bottom: number} | null = null;

    if (neighbour) {
        const contentElement = view.domAtPos(position).node as HTMLElement;
        const contentRect = contentElement.getBoundingClientRect();
        const contentStyle = getComputedStyle(contentElement);
        const leftInset = Number.parseFloat(contentStyle.borderLeftWidth) + Number.parseFloat(contentStyle.paddingLeft);
        const rightInset = Number.parseFloat(contentStyle.borderRightWidth) + Number.parseFloat(contentStyle.paddingRight);
        const neighbourRect = neighbour.getBoundingClientRect();
        const followingRect = nodeBefore && nodeAfter
            ? (view.nodeDOM(position) as HTMLElement | null)?.getBoundingClientRect()
            : undefined;
        const edge = nodeBefore ? neighbourRect.bottom : neighbourRect.top;
        const center = followingRect ? (edge + followingRect.top) / 2 : edge;
        const halfWidth = (PRINTOUT_DROP_CURSOR_WIDTH / 2) * scaleY;

        rect = {
            bottom: center + halfWidth,
            left: contentRect.left + leftInset * scaleX,
            right: contentRect.right - rightInset * scaleX,
            top: center - halfWidth,
        };
    }

    if (!rect) {
        const coords = view.coordsAtPos(position);
        const halfWidth = (PRINTOUT_DROP_CURSOR_WIDTH / 2) * scaleX;

        rect = {bottom: coords.bottom, left: coords.left - halfWidth, right: coords.left + halfWidth, top: coords.top};
    }

    const parent = editorElement.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    const parentLeft = parent && parentRect
        ? parentRect.left - parent.scrollLeft * (parentRect.width / parent.offsetWidth)
        : -window.scrollX;
    const parentTop = parent && parentRect
        ? parentRect.top - parent.scrollTop * (parentRect.height / parent.offsetHeight)
        : -window.scrollY;

    (parent ?? document.body).appendChild(element);
    element.style.left = `${(rect.left - parentLeft) / scaleX}px`;
    element.style.top = `${(rect.top - parentTop) / scaleY}px`;
    element.style.width = `${(rect.right - rect.left) / scaleX}px`;
    element.style.height = `${(rect.bottom - rect.top) / scaleY}px`;
};

export class PrintoutDropCursor {
    private position: number | null = null;
    private element: HTMLElement | null = null;

    constructor(private readonly view: EditorView) {}

    setPosition(position: number | null) {
        if (position === this.position) return;

        this.position = position;

        if (position === null) {
            this.element?.remove();
            this.element = null;
            return;
        }

        if (!this.element) {
            this.element = document.createElement("div");
            this.element.className = PRINTOUT_DROP_CURSOR_CLASS;
            this.element.style.cssText = "position: absolute; z-index: 50; pointer-events: none;";
        }

        drawPrintoutDropCursor(this.view, position, this.element);
    }
}
